import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import type { components } from "@/api/schema.d.ts";
import client from "@/api/openapi-ts";
import { fetchExportDownload } from "@/lib/exports/downloadFile";
import type { paths } from "@/api/schema.d.ts";
import { useErrorStore } from "@/stores/ErrorStore";
import { useExports } from "@/stores/helpers/useExports";

type schemas = components["schemas"];
type Loading = {
  campaigns: boolean;
  campaignDetails: boolean;
  demographicTypes: boolean;
  singleSurveyResults: boolean;
  overallMovement: boolean;
  movementBreakdown: boolean;
  audienceMovement: boolean;
  averageNetMovement: boolean;
  geographyBreakdown: boolean;
  savingName: boolean;
  savingGoal: boolean;
};

type Errors = {
  overallMovement: string | undefined;
  movementBreakdown: string | undefined;
  audienceMovement: string | undefined;
  averageNetMovement: string | undefined;
  geographyBreakdown: string | undefined;
  singleSurveyResults: string | undefined;
};

type ConfigureResponse = {
  status: string;
  locked: boolean;
};

export const useCampaignEffectivenessStore = defineStore(
  "campaignEffectiveness",
  () => {
    const errorStore = useErrorStore();
    const DEFAULT_ERROR_MESSAGE = errorStore.defaultErrorMessage;
    const loading = ref<Loading>({
      campaigns: false,
      campaignDetails: false,
      demographicTypes: false,
      singleSurveyResults: false,
      overallMovement: false,
      movementBreakdown: false,
      audienceMovement: false,
      averageNetMovement: false,
      geographyBreakdown: false,
      savingName: false,
      savingGoal: false,
    });
    const errors = ref<Errors>({
      overallMovement: undefined,
      movementBreakdown: undefined,
      audienceMovement: undefined,
      averageNetMovement: undefined,
      geographyBreakdown: undefined,
      singleSurveyResults: undefined,
    });
    const saving = ref(false);
    const exportManager = useExports<"export">({
      channel: "CampaignEffectiveness",
      keys: ["export"],
      errorMessage: () => DEFAULT_ERROR_MESSAGE,
      // Export failures surface through the store's shared error dialog.
      onError: (_key, message) => {
        errorStore.showError(message);
      },
      fetchFile: (file, signal) =>
        fetchExportDownload(
          "/api/v1/campaign_effectiveness/export/{filename}",
          file,
          signal,
        ),
    });
    const campaigns = ref<schemas["CampaignEffectivenessCampaignsResponse"][]>(
      [],
    );
    const campaignDetails =
      ref<schemas["CampaignEffectivenessCampaignResponse"]>();
    const demographicTypes = ref<schemas["AudienceBuilderDemographicType"][]>(
      [],
    );
    const singleSurveyResults = ref<
      schemas["CampaignEffectivenessSingleSurveyFavorabilityResponse"]
    >({});
    const overallMovement =
      ref<schemas["CampaignEffectivenessOverallMovementResponse"]>();
    const movementBreakdown =
      ref<schemas["CampaignEffectivenessPeopleMovementResponse"]>();
    const audienceMovement =
      ref<schemas["CampaignEffectivenessAudienceMovementResponse"]>();
    const averageNetMovement =
      ref<schemas["CampaignEffectivenessAverageNetMovementResponse"]>();
    const geographyBreakdown =
      ref<schemas["CampaignEffectivenessSingleSurveyGeoBreakdownResponse"]>();
    const updateStatus = (id: number, status: ConfigureResponse) => {
      const campaign = campaigns.value.find((c) => c.id === id);
      if (campaign) {
        campaign.status = status.status;
        campaign.locked = status.locked;
      }
    };

    return {
      loading,
      errors,
      saving,
      campaigns,
      campaignDetails,
      demographicTypes,
      singleSurveyResults,
      overallMovement,
      movementBreakdown,
      audienceMovement,
      averageNetMovement,
      geographyBreakdown,
      exportLoading: exportManager.loading,
      handleExportMessage: exportManager.handleMessage,
      async listCampaigns() {
        loading.value.campaigns = true;
        try {
          const { data } = await client.GET(
            "/api/v1/campaign_effectiveness/campaigns",
          );
          campaigns.value = data ?? [];
        } catch {
          campaigns.value = [];
        } finally {
          loading.value.campaigns = false;
        }
      },
      async getCampaign(id: number) {
        loading.value.campaignDetails = true;
        try {
          const { data } = await client.GET(
            "/api/v1/campaign_effectiveness/campaigns/{campaign_id}",
            {
              params: { path: { campaign_id: id } },
            },
          );
          campaignDetails.value = data;
        } catch {
          campaignDetails.value = undefined;
        } finally {
          loading.value.campaignDetails = false;
        }
      },
      async hasCampaigns(
        org_id: number | undefined,
        app_client_id: number | undefined,
      ) {
        const { data } = await client.GET(
          "/api/v1/campaign_effectiveness/has_campaigns",
          {
            params: { query: { org_id, app_client_id } },
          },
        );
        return data;
      },
      async getDemographicTypes() {
        loading.value.demographicTypes = true;
        try {
          const { data } = await client.GET(
            "/api/v1/campaign_effectiveness/demographic_types",
          );
          demographicTypes.value = data ?? [];
        } catch {
          demographicTypes.value = [];
        } finally {
          loading.value.demographicTypes = false;
        }
      },
      async updateCampaignName(id: number, name: string) {
        loading.value.savingName = true;
        try {
          const response = await client.PUT(
            "/api/v1/campaign_effectiveness/campaigns/{campaign_id}/name",
            {
              params: { path: { campaign_id: id } },
              body: { name },
            },
          );
          if (response.error) {
            throw new Error(response.error.message);
          }

          if (campaignDetails.value && response.data?.name) {
            campaignDetails.value.name = response.data.name;
          }
        } catch (error: any) {
          throw error.message || DEFAULT_ERROR_MESSAGE;
        } finally {
          loading.value.savingName = false;
        }
      },
      async updateGoal(id: number, goal: string) {
        loading.value.savingGoal = true;
        try {
          const { data, error } = await client.PUT(
            "/api/v1/campaign_effectiveness/campaigns/{campaign_id}/goal",
            {
              params: { path: { campaign_id: id } },
              body: { goal },
            },
          );
          if (error) {
            throw error;
          }
          if (campaignDetails.value) {
            campaignDetails.value.goal = data?.goal;
          }
        } catch (error: any) {
          const errorMessage = error?.message || DEFAULT_ERROR_MESSAGE;
          errorStore.showError(errorMessage);
          throw error;
        } finally {
          loading.value.savingGoal = false;
        }
      },
      async configureCampaign(
        id: number,
        config: schemas["CampaignEffectivenessUpdateCampaignRequest"],
      ) {
        saving.value = true;
        try {
          const { data, error } = await client.PUT(
            "/api/v1/campaign_effectiveness/campaigns/{campaign_id}",
            {
              params: { path: { campaign_id: id } },
              body: config,
            },
          );
          if (error) {
            throw error;
          }
          if (data) {
            updateStatus(id, data as ConfigureResponse);
          }
          return data;
        } catch (error: any) {
          const errorMessage = error?.message || DEFAULT_ERROR_MESSAGE;
          errorStore.showError(errorMessage);
          throw error;
        } finally {
          saving.value = false;
        }
      },
      async getSingleSurveyResults(
        params: paths["/api/v1/campaign_effectiveness/single_survey_favorability"]["post"]["requestBody"]["content"]["application/json"],
        forDownload: boolean = false,
      ) {
        if (!forDownload) {
          loading.value.singleSurveyResults = true;
          errors.value.singleSurveyResults = undefined;
        }
        try {
          const { data, error } = await client.POST(
            "/api/v1/campaign_effectiveness/single_survey_favorability",
            {
              body: params,
            },
          );
          if (error) {
            throw error;
          }
          if (forDownload) {
            return data;
          } else {
            singleSurveyResults.value = {
              favorability: data?.favorability ?? {},
            };
          }
        } catch (e: any) {
          const errorMessage = e?.message || DEFAULT_ERROR_MESSAGE;
          errors.value.singleSurveyResults = errorMessage;
        } finally {
          if (!forDownload) {
            loading.value.singleSurveyResults = false;
          }
        }
      },
      async getOverallMovement(kpi_id: number, forDownload: boolean = false) {
        if (!forDownload) {
          loading.value.overallMovement = true;
          errors.value.overallMovement = undefined;
        }
        try {
          const { data, error } = await client.GET(
            "/api/v1/campaign_effectiveness/overall_movement/{campaign_kpi_id}",
            {
              params: { path: { campaign_kpi_id: kpi_id } },
            },
          );
          if (error) {
            throw error;
          }
          if (forDownload) {
            return data;
          } else {
            overallMovement.value = data;
          }
        } catch (e: any) {
          const errorMessage = e?.message || DEFAULT_ERROR_MESSAGE;
          errors.value.overallMovement = errorMessage;
        } finally {
          if (!forDownload) {
            loading.value.overallMovement = false;
          }
        }
      },
      async getMovementBreakdown(
        params: schemas["CampaignEffectivenessPeopleMovementRequest"],
      ) {
        loading.value.movementBreakdown = true;
        errors.value.movementBreakdown = undefined;
        try {
          const { data, error } = await client.POST(
            "/api/v1/campaign_effectiveness/people_movement",
            {
              body: params,
            },
          );
          if (error) {
            throw error;
          }
          movementBreakdown.value = data;
        } catch (e: any) {
          const errorMessage = e?.message || DEFAULT_ERROR_MESSAGE;
          errors.value.movementBreakdown = errorMessage;
        } finally {
          loading.value.movementBreakdown = false;
        }
      },
      async getAudienceMovement(
        params: schemas["CampaignEffectivenessAudienceMovementRequest"],
        forDownload: boolean = false,
      ) {
        if (!forDownload) {
          loading.value.audienceMovement = true;
          errors.value.audienceMovement = undefined;
        }
        try {
          const { data, error } = await client.POST(
            "/api/v1/campaign_effectiveness/audience_movement",
            {
              body: params,
            },
          );
          if (error) {
            throw error;
          }
          if (forDownload) {
            return data;
          } else {
            audienceMovement.value = data;
          }
        } catch (e: any) {
          const errorMessage = e?.message || DEFAULT_ERROR_MESSAGE;
          errors.value.audienceMovement = errorMessage;
        } finally {
          if (!forDownload) {
            loading.value.audienceMovement = false;
          }
        }
      },
      async getAverageNetMovement(
        params: schemas["CampaignEffectivenessAverageNetMovementRequest"],
      ) {
        loading.value.averageNetMovement = true;
        errors.value.averageNetMovement = undefined;
        try {
          const { data, error } = await client.POST(
            "/api/v1/campaign_effectiveness/average_net_movement",
            {
              body: params,
            },
          );
          if (error) {
            throw error;
          }
          averageNetMovement.value = data;
        } catch (e: any) {
          const errorMessage = e?.message || DEFAULT_ERROR_MESSAGE;
          errors.value.averageNetMovement = errorMessage;
        } finally {
          loading.value.averageNetMovement = false;
        }
      },
      async getGeographyBreakdown(
        params: schemas["CampaignEffectivenessSingleSurveyGeoBreakdownRequest"],
      ) {
        loading.value.geographyBreakdown = true;
        errors.value.geographyBreakdown = undefined;
        try {
          const { data, error } = await client.POST(
            "/api/v1/campaign_effectiveness/single_survey_geo_breakdown",
            {
              body: params,
            },
          );
          if (error) {
            throw error;
          }
          if (
            params.geography_breakdown === "counties" &&
            data?.geography_breakdown
          ) {
            const countyData: Record<
              string,
              NonNullable<
                schemas["CampaignEffectivenessSingleSurveyGeoBreakdownResponse"]["geography_breakdown"]
              >[string] & { descriptor: string }
            > = {};
            Object.entries(data.geography_breakdown).forEach(([key, d]) => {
              const fipsAndDescriptor = key.split("-");
              const fips = fipsAndDescriptor[0];
              const descriptor = fipsAndDescriptor[1];
              countyData[fips] = { ...d, descriptor };
            });
            geographyBreakdown.value = {
              ...data,
              geography_breakdown: countyData,
            };
          } else {
            geographyBreakdown.value = data;
          }
        } catch (e: any) {
          const errorMessage = e?.message || DEFAULT_ERROR_MESSAGE;
          errors.value.geographyBreakdown = errorMessage;
        } finally {
          loading.value.geographyBreakdown = false;
        }
      },
      async createExport(
        params: schemas["CampaignEffectivenessExportRequest"],
      ) {
        await exportManager.startExport("export", () =>
          client.POST("/api/v1/campaign_effectiveness/export", {
            body: params,
          }),
        );
      },
    };
  },
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useCampaignEffectivenessStore, import.meta.hot),
  );
}
